import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api, ApiError } from '../../api';
import { Loading, ErrorState, fmtDate } from '../../components/ui';
import { Panel } from '../../components/charts';
import { money } from './Dashboard';

/* ============================================================
   Satu kontrak: BOQ, pengukuran, sertifikat.

   Tombol "Ambil dari verifikasi" adalah jantung modul ini. Ia
   membaca Verification, tidak pernah ProgressClaimLine, dan
   melaporkan berapa baris yang tidak bisa diambil beserta sebabnya
   — item yang tidak tertaut aktivitas, dan aktivitas yang belum
   pernah diverifikasi.
   ============================================================ */

type BoqItem = {
  id: string;
  itemNo: string;
  description: string;
  unit: string;
  contractQty: string | number;
  rate: string | number;
  activity: { activityCode: string; name: string } | null;
  wbsNode: { code: string } | null;
};

type Cert = {
  id: string;
  certNo: number;
  state: string;
  grossToDate: string | number;
  certifiedToDate: string | number;
  netThisCertificate: string | number;
  heldValue: string | number;
  issuedAt: string | null;
  period: { periodNo: number; start: string; end: string };
};

type Variation = {
  id: string;
  voNo: string;
  description: string;
  amount: string | number;
  cause: string;
  status: string;
};

type Period = { id: string; periodNo: number; start: string; end: string; status: string };

const n = (v: string | number) => Number(v);

export function ContractDetail({ projectId }: { projectId: string }) {
  const { id } = useParams();
  const nav = useNavigate();
  const [boq, setBoq] = useState<BoqItem[] | null>(null);
  const [certs, setCerts] = useState<Cert[]>([]);
  const [vos, setVos] = useState<Variation[]>([]);
  const [periods, setPeriods] = useState<Period[]>([]);
  const [periodId, setPeriodId] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [b, c, v, p] = await Promise.all([
        api.get<{ items: BoqItem[] }>(`/cost/contracts/${id}/boq`),
        api.get<{ certificates: Cert[] }>(`/cost/contracts/${id}/certificates`),
        api.get<{ variations: Variation[] }>(`/cost/contracts/${id}/variations`),
        api.get<{ periods: Period[] }>(`/programme/periods?projectId=${projectId}`),
      ]);
      setBoq(b.items);
      setCerts(c.certificates);
      setVos(v.variations);
      setPeriods(p.periods);
      setPeriodId((cur) => cur || (p.periods.find((x) => x.status !== 'CLOSED')?.id ?? ''));
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load the contract.');
    }
  }, [id, projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function measure() {
    if (!periodId) return;
    setBusy(true);
    setNotice(null);
    try {
      const r = await api.post<{ written: number; unlinked: number; unverified: number; items: number }>(
        `/cost/contracts/${id}/measure`,
        { periodId },
      );
      const parts = [`${r.written} dari ${r.items} baris terukur dari verifikasi`];
      if (r.unverified > 0) parts.push(`${r.unverified} belum diverifikasi`);
      if (r.unlinked > 0) parts.push(`${r.unlinked} tidak tertaut aktivitas`);
      setNotice(parts.join(', ') + '.');
      await load();
    } catch (e) {
      setNotice(e instanceof ApiError ? e.message : 'Pengukuran gagal.');
    } finally {
      setBusy(false);
    }
  }

  async function buildCert() {
    if (!periodId) return;
    setBusy(true);
    setNotice(null);
    try {
      const r = await api.post<{
        certificateId: string;
        netThisCertificate: number;
        heldValue: number;
        heldLines: number;
        unmeasuredLines: number;
        lines: number;
      }>(`/cost/contracts/${id}/certificates`, { periodId });
      const parts = [`Sertifikat tersusun: neto ${money(r.netThisCertificate)}`];
      if (r.heldValue > 0) parts.push(`${money(r.heldValue)} tertahan`);
      if (r.unmeasuredLines > 0) {
        // Nilainya nol karena memang belum ada yang diukur, bukan
        // karena tidak ada yang tertahan.
        parts.push(`${r.unmeasuredLines} baris belum terukur sama sekali`);
      }
      setNotice(parts.join(', ') + `. ${r.heldLines} dari ${r.lines} baris tidak disertifikasi.`);
      await load();
      nav(`/certificates/${r.certificateId}`);
    } catch (e) {
      setNotice(e instanceof ApiError ? e.message : 'Penyusunan sertifikat gagal.');
    } finally {
      setBusy(false);
    }
  }

  async function computeEvm() {
    if (!periodId) return;
    setBusy(true);
    try {
      await api.post('/cost/evm', { projectId, periodId });
      setNotice('Earned value dihitung ulang untuk periode ini.');
    } catch (e) {
      setNotice(e instanceof ApiError ? e.message : 'Perhitungan EVM gagal.');
    } finally {
      setBusy(false);
    }
  }

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!boq) return <Loading rows={5} />;

  const boqValue = boq.reduce((s, i) => s + n(i.contractQty) * n(i.rate), 0);
  const linked = boq.filter((i) => i.activity).length;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Kontrak</h1>
          <p>
            {boq.length} item BOQ senilai {money(boqValue)} · {linked} tertaut aktivitas
          </p>
        </div>
        <div className="filters" style={{ marginBottom: 0 }}>
          <select className="select" value={periodId} onChange={(e) => setPeriodId(e.target.value)}>
            <option value="">Pilih periode…</option>
            {periods.map((p) => (
              <option key={p.id} value={p.id}>
                P{p.periodNo} · {p.status}
              </option>
            ))}
          </select>
          <button className="btn btn-ghost btn-sm" disabled={busy || !periodId} onClick={measure}>
            Ambil dari verifikasi
          </button>
          <button className="btn btn-primary btn-sm" disabled={busy || !periodId} onClick={buildCert}>
            Susun sertifikat
          </button>
          <button className="btn btn-ghost btn-sm" disabled={busy || !periodId} onClick={computeEvm}>
            Hitung EVM
          </button>
        </div>
      </div>

      {notice && <div className="banner">{notice}</div>}

      {linked < boq.length && (
        <div className="banner">
          {boq.length - linked} item BOQ belum tertaut ke aktivitas. Item yang tidak tertaut tidak
          bisa diukur dari verifikasi lapangan, dan karena itu tidak akan pernah disertifikasi
          otomatis.
        </div>
      )}

      <Panel title="Bill of quantities" wide>
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Item</th>
                <th>Uraian</th>
                <th>Satuan</th>
                <th>Kuantitas</th>
                <th>Harga satuan</th>
                <th>Nilai</th>
                <th>Aktivitas</th>
              </tr>
            </thead>
            <tbody>
              {boq.map((i) => (
                <tr key={i.id} className="no-click">
                  <td className="num">{i.itemNo}</td>
                  <td>{i.description}</td>
                  <td>{i.unit}</td>
                  <td>{n(i.contractQty).toLocaleString('id-ID')}</td>
                  <td>{n(i.rate).toLocaleString('id-ID')}</td>
                  <td>{money(n(i.contractQty) * n(i.rate))}</td>
                  <td className="hint">
                    {i.activity ? i.activity.activityCode : <span className="chip warn">tidak tertaut</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      <div className="grid-2">
        <Panel title="Sertifikat pembayaran" note="Kumulatif, dikurangi yang sudah disertifikasi.">
          {certs.length === 0 ? (
            <p className="hint">Belum ada. Ukur dulu, lalu susun sertifikat untuk periode terpilih.</p>
          ) : (
            <div className="tbl-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>No</th>
                    <th>Neto</th>
                    <th>Tertahan</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {certs.map((c) => (
                    <tr key={c.id} onClick={() => nav(`/certificates/${c.id}`)}>
                      <td className="num">IPC {c.certNo}</td>
                      <td>{money(n(c.netThisCertificate))}</td>
                      <td className={n(c.heldValue) > 0 ? 'neg' : ''}>
                        {n(c.heldValue) > 0 ? money(n(c.heldValue)) : '—'}
                      </td>
                      <td>
                        <span className={`chip ${c.state === 'DRAFT' ? '' : 'good'}`}>{c.state}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>

        <Panel title="Variation order" note="Yang disetujui ikut naik ke nilai pekerjaan dan ke forecast.">
          {vos.length === 0 ? (
            <p className="hint">Belum ada.</p>
          ) : (
            <div className="tbl-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>No</th>
                    <th>Nilai</th>
                    <th>Sebab</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {vos.map((v) => (
                    <tr key={v.id} className="no-click">
                      <td className="num">{v.voNo}</td>
                      <td>{money(n(v.amount))}</td>
                      <td className="hint">{v.cause.replace(/_/g, ' ').toLowerCase()}</td>
                      <td>
                        <span className={`chip ${v.status === 'APPROVED' ? 'good' : v.status === 'REJECTED' ? 'crit' : ''}`}>
                          {v.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      </div>

      <p className="hint" style={{ marginTop: 16 }}>
        {certs.length > 0 && certs[0]?.issuedAt
          ? `Sertifikat terakhir terbit ${fmtDate(certs[0].issuedAt)}.`
          : 'Sertifikat yang sudah terbit tidak bisa disusun ulang — koreksi terbawa sendiri di periode berikutnya.'}
      </p>
    </>
  );
}
